/**
 * Job card component
 * Card view for a single job
 */

import { Card, Space, Typography, Progress, Tooltip, Dropdown, Button } from 'antd';
import type { MenuProps } from 'antd';
import {
  EyeOutlined,
  DeleteOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  MoreOutlined,
  DownloadOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  FieldTimeOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { JobStatusBadge } from '@/components/common/StatusBadge';
import type { Job } from '@/types/job';
import { formatNumber, formatRelativeTime, formatDuration } from '@/utils/formatters';

const { Text, Title } = Typography;

interface JobCardProps {
  job: Job;
  onDelete?: (id: string) => void;
  onPause?: (id: string) => void;
  onResume?: (id: string) => void;
  onRetry?: (id: string) => void;
  onExport?: (id: string) => void;
}

export function JobCard({
  job,
  onDelete,
  onPause,
  onResume,
  onRetry,
  onExport,
}: JobCardProps) {
  const navigate = useNavigate();

  const progress = job.totalCells > 0
    ? Math.round((job.completedCells / job.totalCells) * 100)
    : 0;

  const getMenuItems = (): MenuProps['items'] => {
    const items: MenuProps['items'] = [];

    if (job.status === 'running') {
      items.push({
        key: 'pause',
        icon: <PauseCircleOutlined />,
        label: 'Pause Job',
        onClick: () => onPause?.(job.id),
      });
    }

    if (job.status === 'paused') {
      items.push({
        key: 'resume',
        icon: <PlayCircleOutlined />,
        label: 'Resume Job',
        onClick: () => onResume?.(job.id),
      });
    }

    if (job.status === 'failed') {
      items.push({
        key: 'retry',
        icon: <ReloadOutlined />,
        label: 'Retry Failed',
        onClick: () => onRetry?.(job.id),
      });
    }

    if (job.status === 'completed' && job.placesFound > 0) {
      items.push({
        key: 'export',
        icon: <DownloadOutlined />,
        label: 'Export Results',
        onClick: () => onExport?.(job.id),
      });
    }

    if (items.length > 0) {
      items.push({ type: 'divider' });
    }

    items.push({
      key: 'delete',
      icon: <DeleteOutlined />,
      label: 'Delete Job',
      danger: true,
      onClick: () => onDelete?.(job.id),
    });

    return items;
  };

  const getDuration = () => {
    if (job.status === 'pending') return null;
    if (job.completedAt && job.startedAt) {
      const duration = new Date(job.completedAt).getTime() - new Date(job.startedAt).getTime();
      return formatDuration(duration);
    }
    if (job.startedAt) {
      return `Running ${formatRelativeTime(job.startedAt)}`;
    }
    return null;
  };

  return (
    <Card
      hoverable
      onClick={() => navigate(`/jobs/${job.id}`)}
      style={{ height: '100%' }}
      styles={{ body: { padding: 20 } }}
      actions={[
        <Tooltip title="View Details" key="view">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/jobs/${job.id}`);
            }}
          />
        </Tooltip>,
        <Dropdown
          key="more"
          menu={{ items: getMenuItems() }}
          trigger={['click']}
          placement="topRight"
        >
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>,
      ]}
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Title
              level={5}
              ellipsis={{ tooltip: job.searchQuery }}
              style={{ margin: 0, marginBottom: 4 }}
            >
              {job.searchQuery}
            </Title>
            {job.name && (
              <Text type="secondary" style={{ fontSize: 13 }}>
                {job.name}
              </Text>
            )}
          </div>
          <JobStatusBadge status={job.status} size="small" />
        </div>

        {/* Progress */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {job.completedCells}/{job.totalCells} cells
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {progress}%
            </Text>
          </div>
          <Progress
            percent={progress}
            size="small"
            status={
              job.status === 'failed'
                ? 'exception'
                : job.status === 'completed'
                ? 'success'
                : 'active'
            }
            showInfo={false}
          />
        </div>

        {/* Stats */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            paddingTop: 8,
            borderTop: '1px solid var(--color-border)',
          }}
        >
          <Tooltip title="Places found">
            <Space size={4}>
              <EnvironmentOutlined style={{ color: 'var(--color-text-tertiary)' }} />
              <Text>{formatNumber(job.placesFound)}</Text>
            </Space>
          </Tooltip>

          <Tooltip title={`Created ${new Date(job.createdAt).toLocaleString()}`}>
            <Space size={4}>
              <ClockCircleOutlined style={{ color: 'var(--color-text-tertiary)' }} />
              <Text type="secondary" style={{ fontSize: 13 }}>
                {formatRelativeTime(job.createdAt)}
              </Text>
            </Space>
          </Tooltip>

          {getDuration() && (
            <Tooltip title="Duration">
              <Space size={4}>
                <FieldTimeOutlined style={{ color: 'var(--color-text-tertiary)' }} />
                <Text type="secondary" style={{ fontSize: 13 }}>
                  {getDuration()}
                </Text>
              </Space>
            </Tooltip>
          )}
        </div>
      </Space>
    </Card>
  );
}

export default JobCard;
